<p class="gear-intro">
  There is no bass amp on Russ's stage. His rig is built entirely from pedals,
  and it is what shows up on professional gigs, in studio sessions, and on the
  lessons end of the Zoom rig.
</p>

<section class="gear-section gear-split" grooveSurface variant="bordered">
  <h2>Basses</h2>
  <p>
    Two
    <a href="https://www.kensmithbasses.com/tiger-elite-series/"
      >Ken Smith Black Tigers</a
    >, a 2008 and a 2021. Russ removed the active electronics from both and runs
    them fully passive, so the tone is shaped downstream in the signal chain
    rather than onboard.
  </p>
  <div class="gear-figures">
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/bass-ks2008.webp"
      alt="2008 Ken Smith Black Tiger bass standing on a stand"
      caption="2008 Ken Smith Black Tiger."
      aspectRatio="550 / 1350"
      sizes="(max-width: 720px) 45vw, 22vw"
    />
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/bass-ks2021.webp"
      alt="2021 Ken Smith Black Tiger bass standing on a stand"
      caption="2021 Ken Smith Black Tiger."
      aspectRatio="550 / 1350"
      sizes="(max-width: 720px) 45vw, 22vw"
    />
  </div>
</section>

<section class="gear-section" grooveSurface variant="bordered">
  <h2>His amp is a pedalboard</h2>
  <p>
    The board comes in two versions, and the gig decides which one comes out.
    The chain is the same on both. The only difference is the Trickfish
    Overdrive Preamp, which the rock and blues board carries and the jazz board
    leaves home.
  </p>
  <app-lightbox-image
    class="gear-photo gear-photo-wide"
    src="/images/gear/amp-board-rock-blues.webp"
    alt="Russ's rock and blues board on a Pedaltrain Metro, left to right: Trickfish Signal Filter, Trickfish External Pre Amp, Boss TU-3S tuner, Trickfish Minnow, Trickfish Overdrive Preamp, and Trickfish VCA Compressor, with the SFX Micro-Thumpinator at the back"
    caption="The rock and blues board."
    aspectRatio="1800 / 618"
    sizes="(max-width: 720px) 100vw, 60vw"
  />
  <app-lightbox-image
    class="gear-photo gear-photo-wide"
    src="/images/gear/amp-board-jazz.webp"
    alt="Russ's jazz board on a Pedaltrain Metro: the same Trickfish chain without the Overdrive Preamp"
    caption="The jazz board. Same chain, no overdrive."
    aspectRatio="1800 / 698"
    sizes="(max-width: 720px) 100vw, 60vw"
  />

  <div class="signal-chain">
    <p class="signal-chain__why">
      Both basses run passive, so the whole tone is built here. The chain is the
      same on both boards, with one exception: the Overdrive Preamp is on the
      rock and blues board only. The last step changes with the gig.
    </p>

    <ol class="signal-chain__flow" aria-label="Core signal chain, in order">
      <li class="signal-chain__stage signal-chain__stage--source">Bass</li>
      <li class="signal-chain__stage">SFX Micro-Thumpinator</li>
      <li class="signal-chain__stage">Trickfish VCA Compressor</li>
      <li class="signal-chain__stage signal-chain__stage--optional">
        Trickfish Overdrive Preamp
      </li>
      <li class="signal-chain__stage">Trickfish Minnow</li>
      <li class="signal-chain__stage">Trickfish External Pre Amp</li>
      <li class="signal-chain__stage">Trickfish Signal Filter</li>
    </ol>

    <ul class="signal-chain__branches">
      <li>
        Live, out of the <strong>Minnow's DI</strong> to front of house, and
      </li>
      <li>
        out of the <strong>Signal Filter</strong> to his
        <strong>RCF powered speakers</strong> (his backline in a box) or his
        in-ears.
      </li>
    </ul>

    <p class="signal-chain__note">
      After the Minnow, the External Pre Amp (bass, mid, and treble) and the
      Signal Filter (a high-pass and low-pass filter) shape what reaches the
      speakers.
    </p>

    <p class="signal-chain__note">
      For recording, a <strong>DI box</strong> taps in right after the
      Micro-Thumpinator; Russ still monitors through the rest of the board to
      his speakers or in-ears.
    </p>

    <p class="signal-chain__note">
      When Russ wants effects, his Pedaltrain Nano board patches in right before
      the Minnow.
    </p>

    <div class="signal-chain__board">
      <p class="signal-chain__board-label groove-type-eyebrow">On the board</p>
      <ul class="board-list">
        <li>
          <a href="https://sfxsound.co.uk/microThumpinator/"
            >SFX Micro-Thumpinator</a
          >
        </li>
        <li>
          <a href="https://www.trickfishamps.com/shop/vca-compressor/"
            >Trickfish VCA Compressor</a
          >
        </li>
        <li>
          <a href="https://www.trickfishamps.com/shop/overdrive-preamp/"
            >Trickfish Overdrive Preamp</a
          >
          (rock and blues board only)
        </li>
        <li>
          <a href="https://www.trickfishamps.com/shop/minnow/"
            >Trickfish Minnow</a
          >
        </li>
        <li>
          <a href="https://www.trickfishamps.com/shop/external-pre-amp-epa/"
            >Trickfish External Pre Amp</a
          >
        </li>
        <li>
          <a href="https://www.trickfishamps.com/shop/signal-filter/"
            >Trickfish Signal Filter</a
          >
        </li>
        <li>
          <a href="https://www.boss.info/global/products/tu-3s/">Boss TU-3S</a>
          tuner
        </li>
      </ul>
    </div>

    <details class="signal-chain__routes">
      <summary>The four routes, in full</summary>
      <p>The Overdrive Preamp stage is the rock and blues board only.</p>
      <ol>
        <li>
          <code>Bass, Micro-Thumpinator, DI box</code>
          (recording)
        </li>
        <li>
          <code
            >Bass, Micro-Thumpinator, VCA Comp, Overdrive Preamp, Minnow's
            DI</code
          >
          (front of house)
        </li>
        <li>
          <code
            >Bass, Micro-Thumpinator, VCA Comp, Overdrive Preamp, Minnow,
            External Pre Amp, Signal Filter, RCF speaker or in-ears</code
          >
          (monitor)
        </li>
        <li>
          <code
            >Bass, Micro-Thumpinator, VCA Comp, Overdrive Preamp, effects board,
            Minnow's DI</code
          >
          (with effects)
        </li>
      </ol>
    </details>
  </div>
</section>

<section class="gear-section gear-split" grooveSurface variant="bordered">
  <h2>Speakers</h2>
  <p>
    Instead of an amp cab, Russ carries RCF powered speakers as his backline. A
    12-inch
    <a href="https://www.rcf.it/en/products/product-detail/art-932-a"
      >ART 932-A</a
    >
    and a 10-inch
    <a href="https://www.rcf.it/en/products/product-detail/art-910-a"
      >ART 910-A</a
    >, and he brings more than one when a room calls for it.
  </p>
  <div class="gear-figures">
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/speaker-art932.webp"
      alt="RCF ART 932-A 12-inch powered speaker"
      caption="RCF ART 932-A (12 inch)."
      aspectRatio="661 / 960"
      sizes="(max-width: 720px) 45vw, 22vw"
    />
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/speaker-art910.webp"
      alt="RCF ART 910-A 10-inch powered speaker"
      caption="RCF ART 910-A (10 inch)."
      aspectRatio="661 / 960"
      sizes="(max-width: 720px) 45vw, 22vw"
    />
  </div>
</section>

<section class="gear-section" grooveSurface variant="bordered">
  <h2>In-ear monitoring</h2>
  <p>
    Russ runs on in-ears instead of stage volume, and mixes his own monitor. A
    wireless pack feeds his in-ear monitors, and he builds the mix himself from
    a small mixer at his feet, so he hears exactly what he needs without leaning
    on the house or a monitor engineer.
  </p>
  <ul class="gear-list">
    <li>Session Ace ESA-V3 in-ear monitors</li>
    <li>Sennheiser wireless rig for the bass</li>
    <li>Shure wireless rig for the in-ear monitors</li>
    <li>
      Mackie
      <a href="https://mackie.com/en/products/mixers/vlz4-series/402VLZ4.html"
        >402VLZ4</a
      >
      mixer with an iPad for his personal monitor mix
    </li>
    <li>
      A
      <a href="https://getbackbeat.com/products/backbeat-g2">BackBeat G2</a>, a
      wearable transducer that lets him feel the low end through his strap
      instead of stage volume
    </li>
  </ul>
  <div class="gear-figures-grid">
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/in-ears.webp"
      alt="Russ's Session Ace ESA-V3 in-ear monitors with a braided cable"
      caption="Session Ace ESA-V3 in-ear monitors."
      aspectRatio="1 / 1"
      fit="cover"
      sizes="(max-width: 768px) 45vw, 22vw"
    />
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/in-ear-rig.webp"
      alt="The wireless rig, with Sennheiser and Shure wireless bodypacks and cases marked Bass and IEM"
      caption="The wireless rig."
      aspectRatio="1 / 1"
      fit="cover"
      sizes="(max-width: 768px) 45vw, 22vw"
    />
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/monitor-mixer.webp"
      alt="A Mackie 402VLZ4 mixer and an iPad for the personal monitor mix"
      caption="His own monitor mix."
      aspectRatio="1 / 1"
      fit="cover"
      sizes="(max-width: 768px) 45vw, 22vw"
    />
    <app-lightbox-image
      class="gear-photo"
      src="/images/gear/back-beat-v2.webp"
      alt="A BackBeat G2 wearable bass transducer in its leather case"
      caption="BackBeat G2."
      aspectRatio="1 / 1"
      fit="cover"
      sizes="(max-width: 768px) 45vw, 22vw"
    />
  </div>
</section>

<section class="gear-section gear-split" grooveSurface variant="bordered">
  <h2>Effects</h2>
  <p>
    A small Pedaltrain Nano board holds the color. It carries a
    <a href="https://www.uaudio.com/products/heavenly-plate-reverb"
      >UAFX Heavenly</a
    >
    plate reverb, a
    <a href="https://www.walrusaudio.com/products/polychrome-analog-flanger"
      >Walrus Polychrome</a
    >
    flanger, an
    <a href="https://www.emmaelectronic.com/discumbobulator"
      >Emma Electronic DiscumBOBulator</a
    >
    auto-wah, and an
    <a href="https://www.ehx.com/products/nano-pog/"
      >Electro-Harmonix Nano POG</a
    >
    octave. The board patches in between the overdrive preamp and the Minnow. An
    <a href="https://www.jimdunlop.com/mxr-bass-synth/">MXR Bass Synth</a>
    patches in when a tune calls for it, rather than living on the board.
  </p>
  <app-lightbox-image
    class="gear-photo gear-photo-wide"
    src="/images/gear/board-effects.webp"
    alt="Pedaltrain Nano effects board holding, left to right, a UAFX Heavenly plate reverb, a Walrus Polychrome flanger, an Emma DiscumBOBulator auto-wah, and an Electro-Harmonix Nano POG octave"
    aspectRatio="1800 / 936"
    sizes="(max-width: 720px) 100vw, 60vw"
  />
</section>

<section class="gear-section" grooveSurface variant="bordered">
  <h2>The rig in the wild</h2>
  <p>A few nights out with the setup.</p>
  <app-image-gallery [images]="galleryImages" />
</section>

<section class="gear-section gear-split" grooveSurface variant="bordered">
  <h2>Accessories</h2>
  <ul class="gear-list">
    <li>
      Mogami Gold
      <a
        href="https://mogamicable.com/category/products/gold_instrument-3-6-10-18-25.php"
        >instrument</a
      >
      and
      <a
        href="https://mogamicable.com/category/products/gold_stage_20-30-50.php"
        >stage XLR</a
      >
      cables
    </li>
    <li>
      Essential Sound
      <a href="https://www.essentialsound.com/musiccord-pro-power-cord.htm"
        >MusicCord-Pro</a
      >
      power cables
    </li>
    <li>
      <a href="https://pedaltrain.com/collections/pedal-boards">Pedaltrain</a>
      pedal boards
    </li>
    <li>AirTurn BT-200 S-2 page turner</li>
    <li>
      <a
        href="https://gruvgear.com/collections/krane-carts/products/amg-500-urban-creator-rig"
        >Gruv Gear cart</a
      >
    </li>
  </ul>
  <app-lightbox-image
    class="gear-photo"
    src="/images/gear/airturn.webp"
    alt="AirTurn BT-200 S-2 Bluetooth page turner"
    caption="AirTurn BT-200 S-2 page turner."
    aspectRatio="1600 / 1286"
    sizes="(max-width: 720px) 60vw, 30vw"
  />
</section>
